import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Radio, InputNumber, DatePicker, Button, Spin, Card } from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import css from "./SettingsComponent.module.css";
import { appSettingsActions } from "../../../store/slices/settings-slices";

const SettingsComponent = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { menuFormat, cycleConfig, cutoffHour, cutoffMinute, leadDays, status } = useSelector((store) => store.appSettings);
    const isLoading = status === "loading";

    const [localFormat, setLocalFormat] = useState(menuFormat);
    const [localCycle, setLocalCycle] = useState({ ...cycleConfig });
    const [localCutoffHour, setLocalCutoffHour] = useState(cutoffHour);
    const [localCutoffMinute, setLocalCutoffMinute] = useState(cutoffMinute);
    const [localLeadDays, setLocalLeadDays] = useState(leadDays);

    useEffect(() => {
        dispatch(appSettingsActions.getAppSettings());
    }, [dispatch]);

    useEffect(() => {
        setLocalFormat(menuFormat);
        setLocalCycle({ ...cycleConfig });
        setLocalCutoffHour(cutoffHour);
        setLocalCutoffMinute(cutoffMinute);
        setLocalLeadDays(leadDays);
    }, [menuFormat, cycleConfig, cutoffHour, cutoffMinute, leadDays]);

    const handleSave = async () => {
        try {
            await dispatch(
                appSettingsActions.updateAppSettings({
                    menuFormat: localFormat,
                    cycleConfig: localCycle,
                    cutoffHour: localCutoffHour,
                    cutoffMinute: localCutoffMinute,
                    leadDays: localLeadDays,
                })
            ).unwrap();
            toast.success(t("Settings saved successfully"));
        } catch (e) {
            toast.error(t("Failed to save settings"));
        }
    };

    const isPeriodBased = localFormat === "period_based";

    return (
        <section className={css.wrap}>
            <Spin spinning={isLoading}>
                <div className={css.row}>
                <Card title={t("Menu Format")} className={css.card}>
                    <div className={css.field}>
                        <label className={css.label}>{t("Menu type")}</label>
                        <Radio.Group
                            value={localFormat}
                            onChange={(e) => setLocalFormat(e.target.value)}
                        >
                            <Radio value="database">{t("Database (date-based)")}</Radio>
                            <Radio value="period_based">{t("Period-based (cycle)")}</Radio>
                        </Radio.Group>
                        <p className={css.hint}>
                            {isPeriodBased
                                ? t("Menu will cycle through Day 1 – Day N and repeat automatically.")
                                : t("Menu is assigned to specific calendar dates.")}
                        </p>
                    </div>

                    {isPeriodBased && (
                        <div >
                            <div>
                                <label className={css.label}>{t("Cycle length (days)")}</label>
                                <InputNumber
                                    min={1}
                                    max={60}
                                    value={localCycle.lengthDays}
                                    onChange={(val) =>
                                        setLocalCycle((prev) => ({ ...prev, lengthDays: val || 7 }))
                                    }
                                />
                            </div>

                            <div className={css.field}>
                                <label className={css.label}>{t("Cycle start date")}</label>
                                <DatePicker
                                    value={localCycle.startDate ? dayjs(localCycle.startDate) : null}
                                    onChange={(_, dateString) =>
                                        setLocalCycle((prev) => ({ ...prev, startDate: dateString || null }))
                                    }
                                    format="YYYY-MM-DD"
                                    style={{ width: 200 }}
                                />
                            </div>

                            <div className={css.field}>
                                <label className={css.label}>{t("Cycle duration (months)")}</label>
                                <InputNumber
                                    min={1}
                                    max={24}
                                    value={localCycle.durationMonths}
                                    onChange={(val) =>
                                        setLocalCycle((prev) => ({ ...prev, durationMonths: val || 3 }))
                                    }
                                />
                            </div>
                        </div>
                    )}

                    <div className={css.actions}>
                        <Button type="primary" onClick={handleSave} loading={isLoading}>
                            {t("Save settings")}
                        </Button>
                    </div>
                </Card>

                <Card title={t("Order Cutoff")} className={css.card}>
                    <p className={css.hint} style={{ marginBottom: 16 }}>
                        {t("After this time, the earliest available delivery date moves forward by one day. Set hour to 24 to disable.")}
                    </p>
                    <div className={css.field}>
                        <label className={css.label}>{t("Cutoff hour (0–24, 24 = disabled)")}</label>
                        <InputNumber
                            min={0}
                            max={24}
                            value={localCutoffHour}
                            onChange={(val) => setLocalCutoffHour(val ?? 24)}
                        />
                    </div>
                    <div className={css.field}>
                        <label className={css.label}>{t("Cutoff minute (0–59)")}</label>
                        <InputNumber
                            min={0}
                            max={59}
                            value={localCutoffMinute}
                            onChange={(val) => setLocalCutoffMinute(val ?? 0)}
                        />
                    </div>
                    <div className={css.field}>
                        <label className={css.label}>{t("Lead days (minimum days before delivery, default 3)")}</label>
                        <InputNumber
                            min={1}
                            max={30}
                            value={localLeadDays}
                            onChange={(val) => setLocalLeadDays(val ?? 3)}
                        />
                    </div>
                    <div className={css.actions}>
                        <Button type="primary" onClick={handleSave} loading={isLoading}>
                            {t("Save settings")}
                        </Button>
                    </div>
                </Card>
                </div>
            </Spin>
        </section>
    );
};

export { SettingsComponent };
